// ** Modules
import React from 'react';
import Image from 'next/image';
import Blockies from 'react-blockies';

// ** UI
import Avatar from '@mui/material/Avatar';

// ** Types
import type { SxProps, Theme } from '@mui/system';

type NextAvatarProps = {
	alt: string;
	src?: string;
	placeholder?: string;
	imageClass?: string;
	data?: any;
	size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'default';
	children?: React.ReactNode;
	sx?: SxProps<Theme>;
};

const NextAvatar = React.forwardRef<HTMLDivElement, NextAvatarProps>(
	({ alt, src, placeholder, imageClass, data, size = 'default', children, sx }: NextAvatarProps, ref) => {
		const format = {
			xs: () => ({
				width: 25,
				height: 25,
				fontSize: 'small',
				canvas: { width: '25px !important', height: '25px !important' }
			}),
			sm: () => ({
				width: 70,
				height: 70,
				canvas: { width: '70px !important', height: '70px !important' }
			}),
			md: () => ({
				width: 120,
				height: 120,
				canvas: { width: '120px !important', height: '120px !important' }
			}),
			lg: () => ({
				width: 170,
				height: 170,
				canvas: { width: '170px !important', height: '170px !important' }
			}),
			xl: () => ({
				width: 250,
				height: 250,
				canvas: { width: '250px !important', height: '250px !important' }
			}),
			default: () => ({
				width: 40,
				height: 40,
				canvas: { width: '40px !important', height: '40px !important' }
			})
		};

		return (
			<Avatar sx={{ ...format[size](), ...sx }} ref={ref}>
				{src && !children ? (
					<Image
						src={src || placeholder || '/avatar_placeholder.svg'}
						alt={alt || 'avatar'}
						className={imageClass ? imageClass : undefined}
						fill // No more width/height errors
						style={{ objectFit: 'cover' }} // Maintains aspect ratio inside the circle
						sizes="(max-width: 768px) 100vw, 33vw" // Helps Next.js optimize the fetch
						placeholder="blur"
						blurDataURL="/avatar_placeholder.svg"
					/>
				) : !children ? (
					<Blockies
						seed={
							data?._id?.toLowerCase() || 'Tischler NRW'
						} /* the only required prop; determines how the image is generated */
						size={10} /* number of squares wide/tall the image will be; default = 15 */
						scale={7} /* width/height of each square in pixels; default = 4 */
						color={data?.blockAvatar?.color || '#E6B194'} /* normal color; random by default */
						bgColor={data?.blockAvatar?.bgColor || '#E48400'} /* background color; random by default */
						spotColor={
							data?.blockAvatar?.spotColor || '#F4C430'
						} /* color of the more notable features; random by default */
						className={
							imageClass ? imageClass : undefined
						} /* optional class name for the canvas element; 'identicon' by default */
					/>
				) : (
					children
				)}
			</Avatar>
		);
	}
);

NextAvatar.displayName = 'NextAvatar';

export default NextAvatar;
